import { inject, Injectable } from "@angular/core";
import { Observable, tap } from "rxjs";
import { createStore, withProps } from "@ngneat/elf";
import { localStorageStrategy, persistState } from "@ngneat/elf-persist-state";

import { selectLoading, withLoading } from "../store/stateprops";
import { ConsentService } from "../services/consent.service";
import { initializeRequestElf } from "../store/state";

export interface ConsentStoreProps {
    hasConsent: boolean | null;
}

@Injectable({ providedIn: "root" })
export class ConsentStoreRepository {
    private readonly consentService = inject(ConsentService);

    private consentStore = this.createStore();

    public isLoading$ = this.consentStore.pipe(selectLoading());

    get hasConsent() {
        return this.consentStore.getValue().hasConsent;
    }

    private createStore(): typeof store {
        const store = createStore(
            { name: 'consent' },
            withProps<ConsentStoreProps>({
                hasConsent: null
            }),
            withLoading()
        );
        persistState(store, {
            storage: localStorageStrategy,
        });
        return store;
    }

    loadHasConsent$(): Observable<boolean> {
        return this.consentService.postRetrievePatientConsent$()
            .pipe(
                initializeRequestElf(this.consentStore),
                tap({
                    next: (value: boolean) => {
                        this.consentStore.update((state) => ({
                            ...state,
                            hasConsent: value
                        }));
                    },
                    error: (error) => {
                        console.error('Error fetching consent:', error);
                    },
                    complete: () => {
                        console.log('Consent recieved');
                    }
                })
            );
    }

    updateHasConsent$(): Observable<boolean> {
        return this.consentService.postApprovePatientConsent$()
            .pipe(
                initializeRequestElf(this.consentStore),
                tap({
                    next: (value: boolean) => {
                        this.consentStore.update((state) => ({
                            ...state,
                            hasConsent: value
                        }))
                    },
                    error: (error) => {
                        console.error('Error approving consent:', error);
                    },
                    complete: () => {
                        console.log('Consent approved');
                    }
                })
            );
    }

    clearConsentStore() {
        this.consentStore.reset();
    }

}